import { Entity, entityImportOnboardPayload } from '../../classes';
import { Entities } from '../../model/entity';
import { GenericEntityImportData, OrgNrDobLenght, defaultHeaders } from '../generic-data';
import {
  isCorrectDateFormat,
  generateDateOfBirth,
  isEmptyRow,
  doEntitiesMatch,
  createUniqueKeyForEntity,
} from '../snapshots/pipeline';
import { v4 as uuid } from 'uuid';

import { capitalizeCompany } from '@orgbrain/lib-util';

class MissingCompanyNameException extends Error {
  public override name: string;
  constructor() {
    super('MissingCompanyName');
    this.name = 'MissingCompanyNameException';
  }
}
class MissingIdentificationException extends Error {
  public override name: string;
  constructor() {
    super('MissingIdentification');
    this.name = 'MissingIdentification';
  }
}

class MissingOrgnumberException extends Error {
  public override name: string;
  constructor() {
    super('MissingOrgNumber');
    this.name = 'MissingOrgnumberException';
  }
}
class MissingAdditionalContactsNameException extends Error {
  public override name: string;
  constructor() {
    super('MissingAdditionalContactsName');
    this.name = 'MissingAdditionalContactsNameException';
  }
}

function fillEmptyRow(row: any, previousRow: any) {
  if (previousRow.orgnr && previousRow.companyname) {
    row.orgnr = previousRow.orgnr;
    row.companyname = previousRow.companyname;
    row.email = previousRow.email;
  }
  row.firstname = previousRow.firstname;
  row.lastname = previousRow.lastname;
  row.email = previousRow.email;
  row.dob = previousRow.dob;
  if (row.pnr) {
    row.pnr = previousRow.pnr;
  }
  return row;
}
function companyCheck(inputData) {
  for (const row of inputData) {
    if (row.companyname && !row.orgnr) {
      console.log('Company Name: ', row.companyname);
      throw new MissingOrgnumberException();
    } else if (row.orgnr && !row.companyname) {
      console.log('Org Nr: ', row.orgnr);

      throw new MissingCompanyNameException();
    }
  }
}
function fillEmptyRows(data: any) {
  let previousRow = data[0];
  for (let row of data) {
    if (isEmptyRow(row)) {
      row = fillEmptyRow(row, previousRow);
    } else {
      previousRow = row;
    }
  }

  return data;
}
function isAdditionalContactRow(row) {
  if (
    row.extraContacts &&
    (row.extraContacts === 'yes' ||
      row.extraContacts === 'ja' ||
      row.extraContacts === 'j' ||
      row.extraContacts === 'y')
  ) {
    return true;
  }
  return false;
}
function createAdditionalContactsField(data: GenericEntityImportData[]) {
  let previousRow = data[0];
  const additionalContacts = [];
  for (const row of data) {
    if (!previousRow.additionalContacts && isAdditionalContactRow(row)) {
      previousRow['additionalContacts'] = [row];
      row['isAdditional'] = true;
    } else if (previousRow.additionalContacts && isAdditionalContactRow(row)) {
      previousRow['additionalContacts'].push(row);
      row['isAdditional'] = true;
    } else if (row.extraContacts) {
      // if we then have a orgnr then this is a contact for that company and need to be registered for that company
      if (
        row.extraContacts !== 'yes' &&
        row.extraContacts !== 'ja' &&
        row.extraContacts !== 'j' &&
        row.extraContacts !== 'y'
      ) {
        //make a mapping between the orgnr and the additional contact to be able to add it to the correct company, we cannot sett this on the orgnr since that will be removed so we will return a map with the orgnr and the additional contact
        additionalContacts[row.extraContacts] = row;
        row['isAdditional'] = true;
      }
      previousRow = row;
    } else {
      previousRow = row;
    }
  }
  if (additionalContacts.length > 0) {
    for (const row of data) {
      if (!row.orgnr) {
        continue;
      }
      if (additionalContacts[row.orgnr]) {
        if (row.additionalContacts) {
          row.additionalContacts.push(additionalContacts[row.orgnr]);
        }
      }
    }
  }

  return data;
}

function removeAdditionalContactsRows(data: GenericEntityImportData[]) {
  return data.filter((row) => !row['isAdditional']);
}

function idCheck(inputData) {
  for (const row of inputData) {
    if (!(row.companyname && row.orgnr) && !row.email && !(row.lastname && row.firstname)) {
      throw new MissingIdentificationException();
    }
  }
}

function markDuplicateNameEntities(entities: Entity[]): Entity[] {
  const entityMap = entities.reduce((acc: any, entity: Entity): any => {
    const name: string = Entities.getPersonEntityName(entity);
    if (acc[name] && !entity.orgnr) {
      acc[name] = acc[name] + 1;
    } else if (!entity.orgnr) {
      acc[name] = 1;
    }

    return acc;
  }, {});

  const newEntities = entities.map((entity: Entity): Entity | any => {
    const name: string = Entities.getPersonEntityName(entity);
    if (entityMap[name] > 1 && !entity.orgnr) {
      return { ...entity, hasDuplicateNames: true };
    }
    return entity;
  });

  return newEntities;
}

function markDuplicateEmailEntities(entities: Entity[]): Entity[] {
  const entityMap = entities.reduce((acc: any, entity: Entity): any => {
    const email: string = Entities.getEntityEmail(entity);
    if (acc[email] && !entity.orgnr) {
      acc[email] = acc[email] + 1;
    } else if (!entity.orgnr) {
      acc[email] = 1;
    }

    return acc;
  }, {});

  const newEntities = entities.map((entity: Entity): Entity | any => {
    const email: string = Entities.getEntityEmail(entity);

    if (entityMap[email] > 1 && !entity.orgnr) {
      return { ...entity, hasDuplicateEmails: true };
    }
    return entity;
  });

  return newEntities;
}

function allCases(string) {
  const upper = /[A-Z]/.test(string),
    lower = /[a-z]/.test(string);

  return upper && lower;
}

function preProcessInputData(inputData, existingEntities) {
  const existingEntitiesWithDuplicateFlags = markDuplicateNameEntities(
    markDuplicateEmailEntities(existingEntities)
  );
  const existingEntitiesCopy = [...existingEntitiesWithDuplicateFlags];
  const existingEntitiesMap = existingEntitiesCopy.reduce((acc, entity) => {
    if (entity.orgnr) {
      acc[entity.orgnr] = entity;
      return acc;
    }
    entity.firstName =
      entity.firstName || (entity.users && entity.users[0] && entity.users[0].firstName);
    entity.lastName =
      entity.lastName || (entity.users && entity.users[0] && entity.users[0].lastName);
    if (entity.firstName && entity.lastName) {
      const name =
        (entity.firstName || '').toLowerCase().trim() +
        ' ' +
        (entity.lastName || '').toLowerCase().trim();
      acc[name] = entity;
    }
    if ((entity && entity.users && entity.users[0] && entity.users[0].email) || entity.email) {
      acc[
        (entity && entity.users && entity.users[0] && entity.users[0].email.toLowerCase().trim()) ||
          entity.email.toLocaleLowerCase().trim()
      ] = entity;
    }
    if (entity.dob && isCorrectDateFormat(entity.dob)) {
      acc[generateDateOfBirth(entity)] = entity;
    }

    return acc;
  }, {});

  const newEntitiesWithDuplicateFlags = markDuplicateNameEntities(
    markDuplicateEmailEntities(
      inputData.map((x) => {
        return {
          ...x,
          lastName: capitalizeCompany(x.lastname || ''),
          firstName: capitalizeCompany(x.firstname || ''),
        };
      })
    )
  );

  const data = [];

  for (const item of newEntitiesWithDuplicateFlags) {
    const newItem = {
      ...item,
      _id: uuid(),
      isCompany: !!item.companyname,
    };
    delete (newItem as any).firstname;
    delete (newItem as any).lastname;

    if (newItem.isCompany) {
      if (!allCases(newItem.companyname)) {
        newItem.companyname = capitalizeCompany(item.companyname || '');
      }
      newItem.orgnr = item.orgnr;
    } else {
      if (newItem.dob) {
        newItem.dob = generateDateOfBirth(newItem);
      }
    }
    const existingEntity = doEntitiesMatch(existingEntitiesMap, newItem);

    if (existingEntity) {
      const temp = { ...existingEntity };

      if (newItem.email) {
        temp.email = newItem.email;
        existingEntity.email = newItem.email;
      }
      if (newItem.pnr) {
        temp.pnr = newItem.pnr;
        existingEntity.pnr = newItem.pnr;
      }
      if (newItem.address) {
        temp.address = newItem.address;
        existingEntity.address = newItem.address;
      }
      if (newItem.dob) {
        temp.dob = newItem.dob;
        existingEntity.dob = newItem.dob;
      }
      if (newItem.banknr) {
        temp.banknr = newItem.banknr;
        existingEntity.banknr = newItem.banknr;
      }
      if (newItem.zip) {
        temp.zip = newItem.zip;
        existingEntity.zip = newItem.zip;
      }
      if (newItem.city) {
        temp.city = newItem.city;
        existingEntity.city = newItem.city;
      }
      if (newItem.tlf) {
        temp.tlf = newItem.tlf;
        existingEntity.tlf = newItem.tlf;
      }
      if (newItem.country) {
        temp.country = newItem.country;
        existingEntity.country = newItem.country;
      }
      if (newItem.vps) {
        temp.vps = newItem.vps;
        existingEntity.vps = newItem.vps;
      }
      if (newItem.lei) {
        temp.lei = newItem.lei;
        existingEntity.lei = newItem.lei;
      }

      data.push({
        ...temp,
        shares: (newItem as any).shares,
        records: (newItem as any).records,
        additionalContacts: (newItem as any).additionalContacts,
      });
    } else {
      data.push({ ...newItem, shares: (newItem as any).shares, createMe: true });
    }
  }

  //let mergedInputData: any[] = mergeInputData(inputData);
  return [data, existingEntitiesWithDuplicateFlags];
}
function extractAdditionalContacts(inputData) {
  return inputData.reduce((acc, entity) => {
    if (!entity.additionalContacts) {
      return acc;
    }
    acc[entity._id] = entity.additionalContacts.map((contact) => {
      const newContact = {
        ...contact,
        firstName: contact.firstName || contact.firstname,
        lastName: contact.lastName || contact.lastname,
      };

      delete newContact.lastname;
      delete newContact.firstname;
      delete newContact.isAdditional;
      delete newContact.type;
      if (newContact.dob) {
        delete newContact.dob;
      }

      if (!newContact.name && (!newContact.firstName || !newContact.lastName)) {
        throw new MissingAdditionalContactsNameException();
      }
      return newContact;
    });

    return acc;
  }, {});
}
function processEntities(inputData) {
  return inputData.map((entity) => {
    delete entity.additionalContacts;
    delete entity.records;
    delete entity.type;
    return entity;
  });
}

export function buildEntityOnboardPayload(
  data: GenericEntityImportData[],
  existingEntities: Entity[]
): { payload: entityImportOnboardPayload; metaData: any } {
  const [preProcessedInputData, existingEntitiesWithDuplicateFlags]: any[][] = preProcessInputData(
    data,
    existingEntities
  );

  const metaData: any = {
    newClasses: 0,
    newOwners: preProcessedInputData.filter((entity) => entity.createMe).length,
    existingOwners: preProcessedInputData.filter((entity) => !entity.createMe).length,
  };

  const additionalContacts: { [key: string]: any } =
    extractAdditionalContacts(preProcessedInputData);

  const entities = processEntities(preProcessedInputData);

  return {
    payload: {
      entities,
      additionalContacts,
    },
    metaData,
  };
}

function generateEntityLookup(data, keyGeneratorFunction, usenomenee = true) {
  const entityLookup: { [key: string]: GenericEntityImportData[] } = {};
  const keyused: { [key: string]: number } = {};

  for (const item of data) {
    let key = keyGeneratorFunction(item);
    if (entityLookup[key]) {
      if (item.accounttype && usenomenee) {
        if (
          item.accounttype.toLowerCase() === 'nominee' ||
          item.accounttype.toLowerCase() === 'megler'
        ) {
          item.orgnr = generateNewOrgNr(item, key, keyused);
          key = keyGeneratorFunction(item);
          keyused[key] = 1;
          entityLookup[key] = [item];
        } else {
          entityLookup[key].push(item);
        }
      } else {
        entityLookup[key].push(item);
      }
    } else {
      entityLookup[key] = [item];
    }
  }

  return entityLookup;
}

function generateNewOrgNr(item, key, keyused) {
  //if we have a used a key before, we need to generate the next keynumber with the orgnumber
  if (keyused[key]) {
    keyused[key] = keyused[key] + 1;
  } else {
    keyused[key] = 1;
  }
  return item.orgnr + '-' + keyused[key];
}

export function mergeDuplicateEntityRows(data: GenericEntityImportData[]) {
  const newData: GenericEntityImportData[] = [];
  const duplicateRows = [];

  const entityLookup = generateEntityLookup(data, createUniqueKeyForEntity);

  const keys = Object.keys(entityLookup);
  let count = 0;
  for (const key of keys) {
    count++;

    const rows = entityLookup[key];
    const newRow = rows[0];
    const rowStringlist = [];
    if (rows.length !== 1) {
      // for each duplicate row,
      rows.forEach((row) => {
        // merge the data exept for the shareamount and sharenotes into a string
        //do not need to look at amount or notes since they are most likely different
        const { _linenumber, ...rest } = row;
        //take out any fields that are not in the generic data
        for (const key of Object.keys(rest)) {
          if (!defaultHeaders.includes(key)) {
            delete rest[key];
          }
        }
        const rowString = JSON.stringify(rest);
        // if the string is not already in the duplicateRows array, add it
        if (!rowStringlist.includes(rowString)) {
          rowStringlist.push(rowString);
        }
      });
      if (rowStringlist.length > 1) {
        duplicateRows.push(rows);
      }
    }

    newData.push(newRow);
  }
  return { payload: newData, duplicateRows };
}
export function scanAndChangeOrgAndDob(data: GenericEntityImportData[]): GenericEntityImportData[] {
  const countOrgDob = data.filter((data) => data.orgnrdob).length;
  if (countOrgDob > 0) {
    for (const row of data) {
      //strip the - , . and spaces from the orgnrdob to test the length
      if (!row.orgnrdob) {
        if (!row.orgnr && !row.pnr && !row.dob) {
          console.log('Error row:', row);
          throw new MissingOrgnumberException();
        }
        continue;
      }

      const orgnrdob = row.orgnrdob.replace(/[-., ]+/g, '');
      switch (orgnrdob.length) {
        case OrgNrDobLenght.DOBANDPNR:
          row.dob = (row.orgnrdob + '').substring(0, 6);
          row.pnr = (row.orgnrdob + '').substr(-5);
          break;
        case OrgNrDobLenght.DOB:
        case OrgNrDobLenght.DOBYEAR:
          row.dob = row.orgnrdob;
          break;
        case OrgNrDobLenght.ORGNR:
          row.orgnr = row.orgnrdob;
          if (!row.companyname) {
            row.companyname = row['name'];
          }
          break;
        default:
          if (row.pnr) {
            if (!row.dob && row.pnr.length === OrgNrDobLenght.DOBANDPNR) {
              row.dob = (row.pnr + '').substring(0, 6);
            }
            break;
          }
          if (!row.companyname) {
            row.companyname = row['name'];
          }
          row.orgnr = row.orgnrdob;
          break;
      }
      delete row.orgnrdob;
    }
  }
  return data;
}

export function scanAndChangeFullEntityName(
  data: GenericEntityImportData[]
): GenericEntityImportData[] {
  for (const row of data) {
    if (row.name) {
      try {
        let names: string[], firstname: string, lastname: string;

        if (row.name.indexOf(',') !== -1) {
          names = row.name.trim().split(',');
          firstname = names[1];
          lastname = names[0];
        } else {
          names = row.name.trim().split(' ');
          firstname = names.slice(0, -1).join(' ');
          lastname = names[names.length - 1];
        }

        if (row.firstname) {
          row.lastname = row.name;
        } else {
          row.firstname = firstname;
          row.lastname = lastname;
        }

        delete row.name;
      } catch (error: any) {
        row.firstname = row.name;
      }
    }
  }
  return data;
}

export function entityImportPipeline(
  data: GenericEntityImportData[],
  existingEntities: Entity[]
): { payload: entityImportOnboardPayload; metaData: any } {
  let output: GenericEntityImportData[] = scanAndChangeOrgAndDob(data);
  output = scanAndChangeFullEntityName(output);

  output = fillEmptyRows(output);
  const res: any = mergeDuplicateEntityRows(output);

  //find out if there is any shacreclasses in the data

  let payload = createAdditionalContactsField(res.payload);
  payload = removeAdditionalContactsRows(res.payload);

  companyCheck(payload);

  idCheck(payload);

  const result = buildEntityOnboardPayload(payload, existingEntities);
  if (res.duplicateRows.length > 0) {
    result.metaData.duplicateRows = res.duplicateRows;
  }

  return result;
}
